import React, { useState, useEffect } from 'react'
import { Truck, Package, Loader2 } from 'lucide-react'
import { useWholesale } from '../contexts/WholesaleContext'

function useDeliveryCountdown() {
  const [now, setNow] = useState(new Date())

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000)
    return () => clearInterval(interval)
  }, [])

  const ptHour = (h: number) => {
    // Convert PT to UTC. PT is UTC-7 (PDT) or UTC-8 (PST).
    // For simplicity, use UTC-7 (PDT, which is current during summer).
    return h + 7
  }

  // Local cutoff: Sunday 5pm PT = Sunday 17:00 PT = Sunday 24:00 UTC (PDT)
  // UPS cutoff: Tuesday 5pm PT = Tuesday 17:00 PT = Wednesday 00:00 UTC (PDT)
  const getNextCutoff = (dayOfWeek: number, hourPT: number) => {
    const cutoff = new Date(now)
    cutoff.setUTCHours(0, 0, 0, 0)
    const currentDay = cutoff.getUTCDay()
    let daysUntil = (dayOfWeek - currentDay + 7) % 7
    if (daysUntil === 0) {
      // Same day — check if cutoff has passed
      const cutoffUTC = ptHour(hourPT)
      if (now.getUTCHours() >= cutoffUTC) {
        daysUntil = 7
      }
    }
    cutoff.setUTCDate(cutoff.getUTCDate() + daysUntil)
    cutoff.setUTCHours(ptHour(hourPT), 0, 0, 0)
    return cutoff
  }

  const localCutoff = getNextCutoff(0, 17) // Sunday 5pm PT
  const upsCutoff = getNextCutoff(2, 17)   // Tuesday 5pm PT

  const formatCountdown = (cutoff: Date) => {
    const diff = cutoff.getTime() - now.getTime()
    if (diff <= 0) return 'Cutoff passed — next week'
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    if (days > 0) return `${days}d ${hours}h`
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    return `${hours}h ${mins}m`
  }

  return {
    localCountdown: formatCountdown(localCutoff),
    upsCountdown: formatCountdown(upsCutoff),
  }
}

const WholesaleOrderSummary: React.FC = () => {
  const {
    selectedKit, quote, totalAmount, quantity,
    couponInput, setCouponInput, applyCoupon, removeCoupon,
    couponCode, couponError, couponChecking, couponDiscountType,
    deliveryMethod, setDeliveryMethod,
  } = useWholesale()

  const { localCountdown, upsCountdown } = useDeliveryCountdown()

  const subtotal = quote ? quote.subtotal : selectedKit ? selectedKit.price * quantity : 0
  const showDeliverySelector = quote?.localDeliveryAvailable ?? false
  const savings = quote ? quote.upsShippingCost - quote.localDeliveryCost : 0

  if (!selectedKit) return null

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
      <h3 className="text-lg font-bold !text-white mb-4 font-heading">
        Order Summary
      </h3>

      <div className="space-y-3">
        {/* Kit line item with quantity */}
        <div className="flex justify-between items-center text-base">
          <span className="!text-white/70">
            {quantity} × {selectedKit.label} Kit
          </span>
          <span className="font-semibold !text-white">
            ${(selectedKit.price * quantity).toFixed(2)}
          </span>
        </div>

        {/* Subtotal */}
        <div className="flex justify-between items-center text-base">
          <span className="!text-white/70">Subtotal</span>
          <span className="font-semibold !text-white">${subtotal.toFixed(2)}</span>
        </div>

        {/* Coupon Input */}
        <div className="pt-2">
          {couponCode && quote && quote.discountAmount > 0 ? (
            <div className="flex items-center justify-between bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-2">
              <span className="text-sm !text-green-400 font-semibold">
                ✓ {couponCode} applied
              </span>
              <button
                onClick={removeCoupon}
                className="text-xs !text-white/50 hover:!text-white transition-colors"
              >
                Remove
              </button>
            </div>
          ) : couponCode && !quote ? (
            <div className="flex items-center justify-between bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-2">
              <span className="text-sm !text-green-400 font-semibold">
                ✓ {couponCode} — applied once you add your shipping ZIP
              </span>
              <button
                onClick={removeCoupon}
                className="text-xs !text-white/50 hover:!text-white transition-colors"
              >
                Remove
              </button>
            </div>
          ) : couponCode && quote && quote.discountAmount === 0 ? (
            <div className="flex items-center justify-between bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
              <span className="text-sm !text-amber-400 font-semibold">
                {couponDiscountType === 'free_local_delivery' && deliveryMethod === 'ups' && quote.localDeliveryAvailable
                  ? `${couponCode} — switch to Local Courier to use this coupon`
                  : couponDiscountType === 'free_local_delivery' && deliveryMethod === 'ups' && !quote.localDeliveryAvailable
                  ? `${couponCode} — local delivery not available for your ZIP code`
                  : couponDiscountType === 'free_shipping' && deliveryMethod === 'local'
                  ? `${couponCode} — switch to UPS 2-Day Air to use this coupon`
                  : `${couponCode} — doesn't apply to your current delivery method`}
              </span>
              <button
                onClick={removeCoupon}
                className="text-xs !text-white/50 hover:!text-white transition-colors"
              >
                Remove
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                type="text"
                autoComplete="off"
                value={couponInput}
                onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => { if (e.key === 'Enter' && !couponChecking) applyCoupon() }}
                placeholder="Promo code"
                readOnly={couponChecking}
                className="flex-1 px-4 py-3 text-base border-2 rounded-xl focus:outline-none bg-white/10 !text-white placeholder-white/40 border-white/20 focus:border-[#962831] read-only:opacity-100"
              />
              <button
                onClick={applyCoupon}
                disabled={couponChecking || !couponInput.trim()}
                className="px-5 py-3 rounded-xl border-2 border-white/20 bg-white/10 hover:bg-white/20 !text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {couponChecking && <Loader2 className="w-4 h-4 animate-spin" />}
                {couponChecking ? 'Checking…' : 'Apply'}
              </button>
            </div>
          )}
          {couponError && (
            <p className="text-xs !text-red-400 mt-1">{couponError}</p>
          )}
        </div>

        {/* Discount */}
        {quote && quote.discountAmount > 0 && (
          <div className="flex justify-between items-center text-base">
            <span className="!text-green-400">Discount ({quote.couponCode})</span>
            <span className="font-semibold !text-green-400">−${quote.discountAmount.toFixed(2)}</span>
          </div>
        )}

        {/* Delivery Method Selector */}
        {showDeliverySelector ? (
          <div className="pt-2 space-y-2">
            <div className="text-sm font-semibold !text-white/70">Delivery Method</div>

            {/* UPS Option */}
            <button
              onClick={() => setDeliveryMethod('ups')}
              className={`w-full text-left rounded-lg border p-3 transition-colors ${
                deliveryMethod === 'ups'
                  ? 'border-[#962831] bg-[#962831]/10'
                  : 'border-white/10 bg-white/5 hover:bg-white/10'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Truck className="w-4 h-4 !text-white/70" />
                  <span className="text-sm font-semibold !text-white">UPS 2-Day Air</span>
                  {quote?.recommendedMethod === 'ups' && (
                    <span className="text-xs bg-[#DAA520]/20 !text-[#DAA520] px-2 py-0.5 rounded-full font-semibold">
                      ★ Recommended
                    </span>
                  )}
                </div>
                <span className="font-semibold !text-white">
                  ${quote?.upsShippingCost.toFixed(2) ?? '—'}
                </span>
              </div>
              <div className="mt-1 ml-6 text-xs !text-white/50">
                Ships Wed · Arrives Fri · Order by Tue 5pm PT ({upsCountdown})
              </div>
            </button>

            {/* Local Delivery Option */}
            <button
              onClick={() => setDeliveryMethod('local')}
              className={`w-full text-left rounded-lg border p-3 transition-colors ${
                deliveryMethod === 'local'
                  ? 'border-[#962831] bg-[#962831]/10'
                  : 'border-white/10 bg-white/5 hover:bg-white/10'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 !text-white/70" />
                  <span className="text-sm font-semibold !text-white">Local Courier</span>
                  {quote?.recommendedMethod === 'local' && (
                    <span className="text-xs bg-[#DAA520]/20 !text-[#DAA520] px-2 py-0.5 rounded-full font-semibold">
                      ★ Recommended
                    </span>
                  )}
                </div>
                <span className="font-semibold !text-white">
                  ${quote?.localDeliveryCost.toFixed(2) ?? '—'}
                </span>
              </div>
              <div className="mt-1 ml-6 text-xs !text-white/50">
                Delivered Monday · Order by Sun 5pm PT ({localCountdown})
              </div>
            </button>

            {/* Savings callout */}
            {savings > 0 && deliveryMethod === quote?.recommendedMethod && (
              <p className="text-xs !text-green-400 font-semibold pl-1">
                You save ${savings.toFixed(2)} with {quote.recommendedMethod === 'local' ? 'local courier' : 'UPS'}
              </p>
            )}
          </div>
        ) : (
          /* Shipping (no local available) */
          <div className="flex justify-between items-center text-base">
            <span className="!text-white/70">Shipping</span>
            {quote ? (
              <span className="font-semibold !text-white">${quote.shippingCost.toFixed(2)}</span>
            ) : (
              <span className="!text-white/50 text-sm">Calculated at checkout</span>
            )}
          </div>
        )}

        {/* Tax */}
        <div className="flex justify-between items-center text-base">
          <span className="!text-white/70">Tax</span>
          {quote ? (
            <span className="font-semibold !text-white">${quote.taxAmount.toFixed(2)}</span>
          ) : (
            <span className="!text-white/50 text-sm">Calculated at checkout</span>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-white/20 pt-3 mt-3">
          <div className="flex justify-between items-center text-xl font-bold">
            <span className="!text-white">Total:</span>
            <span className="!text-white">${totalAmount.toFixed(2)}</span>
          </div>
          <p className="mt-3 text-xs !text-white/50">
            {deliveryMethod === 'local'
              ? 'Local courier delivery on Monday. Sealed with dry ice for food safety.'
              : 'Ships frozen via UPS 2-Day in insulated Uline shipper with dry ice.'}
          </p>
        </div>
      </div>
    </div>
  )
}

export default WholesaleOrderSummary
